import { useStore } from "@tanstack/react-form";

import { useFieldContext } from "..";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { getErrorMessage } from "../utils";
import FieldWrapper from "./FieldWrapper";

type SelectOption = {
  value: undefined | null | string | number;
  label: string;
  disabled?: boolean;
};

type SelectFieldProps = {
  label?: string;
  subLabel?: string;
  placeholder?: string;
  options: SelectOption[];
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
  errorText?: string;
} & React.ComponentProps<typeof Select>;

export const SelectField = ({
  label,
  subLabel,
  placeholder = "Select an option",
  options,
  loading = false,
  loadingText = "Loading...",
  emptyText = "No options available",
  errorText = "Error loading options",
  required,
  ...selectProps
}: SelectFieldProps) => {
  const field = useFieldContext<string>();

  const errors = useStore(field.store, (state) => state.meta.errors);

  const hasError = errors.length > 0 && field.state.meta.isDirty;

  // Helper function to render options based on state
  const renderOptions = () => {
    if (loading) {
      return (
        <SelectItem value="loading" disabled>
          {loadingText}
        </SelectItem>
      );
    }

    if (options.length === 0) {
      return (
        <SelectItem value="empty" disabled>
          {emptyText}
        </SelectItem>
      );
    }

    return options.map((option) => (
      <SelectItem
        key={option.value?.toString()}
        value={option.value?.toString() || ""}
        disabled={option.disabled}
      >
        {option.label}
      </SelectItem>
    ));
  };

  return (
    <FieldWrapper
      id={field.name}
      label={label}
      subLabel={subLabel}
      isRequired={required}
      errorMessage={hasError ? getErrorMessage(errors[0]) : undefined}
    >
      <Select
        value={field?.state?.value || ""}
        onValueChange={(value) => field.handleChange(value)}
        {...selectProps}
      >
        <SelectTrigger
          id={field.name}
          className="w-full"
          aria-invalid={hasError}
          onBlur={field.handleBlur}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>{renderOptions()}</SelectContent>
      </Select>
    </FieldWrapper>
  );
};
